import { impersonationService } from '../../services/impersonation.service';
import { impersonationConstant } from '../constants';
import { saveUser } from './auth';
import { getPermissionsByEmailId } from './sidebar.action';

const ORIGINAL_USER_KEY = 'originalUser';
const ORIGINAL_USER_DETAILS_KEY = 'originalUserDetails';
const IMPERSONATION_KEY = 'impersonation';
const USER_KEY = 'user';
const USER_DETAILS_KEY = 'userDetails';
const CURRENT_COMPANY_KEY = 'currentCompany';

const readJson = (key) => {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const TZ_OFFSET_RE = /[+-]\d{2}:?\d{2}$/;

// Backend returns expiresAt without a timezone designator (e.g. "2026-06-11T09:24:58.96"),
// which JavaScript would otherwise parse as local time. Treat it as UTC.
const normalizeExpiresAt = (value) => {
    if (!value || typeof value !== 'string') return value || null;
    if (value.endsWith('Z') || value.endsWith('z')) return value;
    if (TZ_OFFSET_RE.test(value)) return value;
    return `${value}Z`;
};

const buildLoginShapedPayload = (entity) => ({
    Value: {
        ...entity.user,
        Token: entity.token,
    },
});

const buildImpersonationMeta = (entity, originContext) => ({
    sessionToken: entity?.impersonation?.sessionToken || null,
    expiresAt: normalizeExpiresAt(entity?.impersonation?.expiresAt),
    impersonatedByUserId: entity?.impersonation?.impersonatedByUserId
        ?? entity?.user?.impersonatedByUserId
        ?? null,
    impersonatedByUserName: entity?.impersonation?.impersonatedByUserName
        || entity?.user?.impersonatedByUserName
        || null,
    targetUserId: entity?.impersonation?.targetUserId ?? entity?.user?.employeeId ?? null,
    targetUserName: entity?.impersonation?.targetUserName
        || entity?.user?.userName
        || entity?.user?.emailId
        || null,
    isReadOnly: entity?.impersonation?.isReadOnly !== false,
    originContext: originContext || null,
});

const restoreOriginalSession = (dispatch) => {
    const originalUser = localStorage.getItem(ORIGINAL_USER_KEY);
    const originalUserDetails = localStorage.getItem(ORIGINAL_USER_DETAILS_KEY);
    if (originalUser) localStorage.setItem(USER_KEY, originalUser);
    else localStorage.removeItem(USER_KEY);
    if (originalUserDetails) localStorage.setItem(USER_DETAILS_KEY, originalUserDetails);
    else localStorage.removeItem(USER_DETAILS_KEY);
    localStorage.removeItem(ORIGINAL_USER_KEY);
    localStorage.removeItem(ORIGINAL_USER_DETAILS_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem(CURRENT_COMPANY_KEY);
    const restoredRaw = originalUserDetails || originalUser;
    let restored = null;
    if (restoredRaw) {
        try {
            restored = JSON.parse(restoredRaw);
        } catch {
            restored = null;
        }
    }
    if (restored) dispatch(saveUser(restored));
    dispatch({ type: impersonationConstant.IMPERSONATION_END });
    dispatch(getPermissionsByEmailId());
};

export const startImpersonation = ({ targetUserId, originContext }) => {
    return (dispatch) => {
        return impersonationService
            .startImpersonation({ targetUserId })
            .then((result) => {
                const entity = result?.data?.Entity || result?.data?.entity;
                if (!entity?.token) {
                    throw new Error('Invalid impersonation response from server');
                }

                const currentUser = localStorage.getItem(USER_KEY);
                const currentUserDetails = localStorage.getItem(USER_DETAILS_KEY);
                if (currentUser && !localStorage.getItem(ORIGINAL_USER_KEY)) {
                    localStorage.setItem(ORIGINAL_USER_KEY, currentUser);
                }
                if (currentUserDetails && !localStorage.getItem(ORIGINAL_USER_DETAILS_KEY)) {
                    localStorage.setItem(ORIGINAL_USER_DETAILS_KEY, currentUserDetails);
                }

                const newUserPayload = buildLoginShapedPayload(entity);
                const serialized = JSON.stringify(newUserPayload);
                localStorage.setItem(USER_KEY, serialized);
                localStorage.setItem(USER_DETAILS_KEY, serialized);

                const impersonationMeta = buildImpersonationMeta(entity, originContext);
                localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationMeta));

                dispatch(saveUser(newUserPayload));
                dispatch({
                    type: impersonationConstant.IMPERSONATION_START,
                    payload: impersonationMeta,
                });
                dispatch(getPermissionsByEmailId());

                return impersonationMeta;
            });
    };
};

export const endImpersonation = () => {
    return (dispatch) => {
        const meta = readJson(IMPERSONATION_KEY);
        return impersonationService
            .endImpersonation()
            .catch(() => null)
            .then(() => {
                restoreOriginalSession(dispatch);
                return meta;
            });
    };
};

export const forceEndImpersonation = () => {
    return (dispatch) => {
        const meta = readJson(IMPERSONATION_KEY);
        restoreOriginalSession(dispatch);
        return meta;
    };
};

export const hydrateImpersonationFromStorage = () => {
    return (dispatch) => {
        const meta = readJson(IMPERSONATION_KEY);
        if (meta) {
            const normalized = { ...meta, expiresAt: normalizeExpiresAt(meta.expiresAt) };
            if (normalized.expiresAt !== meta.expiresAt) {
                localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(normalized));
            }
            dispatch({
                type: impersonationConstant.IMPERSONATION_HYDRATE,
                payload: normalized,
            });
        }
    };
};
